import { useEffect, useMemo, useState } from 'react';

import { PermissionGate } from '../../../../auth/PermissionGate';
import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { ApiError } from '../../../../lib/apiError';
import { surveyQuestionsApi } from '../../survey/surveyApi';
import type { SurveyQuestion } from '../../survey/surveyTypes';
import type { PersonnelDetail, PersonnelSurveyAnswer } from '../personnelTypes';
import { surveyFollowUps } from '../utils/surveyFollowUps';

/**
 * The survey — CS Form 212, items 34 to 40.
 *
 * **The panel lists the QUESTIONNAIRE, not the answers.** Every active question is a row whether
 * or not this employee has answered it, with the answer under the question and an Update at the
 * outer right. That is the shape a survey has on paper, and it is what makes completeness
 * readable: an unanswered item is a row saying so, in its own place in the form's order.
 *
 * It replaces a table of recorded answers with a Create button. That table started empty on
 * every record and said "0 records" over twelve outstanding questions — the reader had to open a
 * modal, pick a question from a dropdown, answer it and repeat, with nothing on screen saying
 * which ones were left.
 *
 * **So there is no Create, no search, no pager and no empty state.** Nothing is created here —
 * the questions come from Personnel → Settings → Survey — and a list that is always exactly as
 * long as the questionnaire has nothing to page and nothing a search would narrow that is not
 * already on screen.
 */
export function SurveyPanel({
  record,
  onUpdate,
}: {
  record: PersonnelDetail;
  /** Opens the answer form for one question, seeded with its answer where there is one. */
  onUpdate: (question: SurveyQuestion, answer: PersonnelSurveyAnswer | null) => void;
}) {
  const [questions, setQuestions] = useState<SurveyQuestion[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);

    surveyQuestionsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setQuestions(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load the survey.', 0),
        );
      });

    return () => controller.abort();
  }, [reloadKey]);

  /*
    The LIVE answer per question. A deleted one is not an answer this record holds, so its row
    reads as unanswered — which is what the reader needs to see, and it is why the lookup is by
    question rather than a list of answers.
  */
  const answers = useMemo(() => {
    const byQuestion = new Map<string, PersonnelSurveyAnswer>();

    for (const answer of record.surveyAnswers) {
      if (answer.isActive) byQuestion.set(answer.surveyQuestionId, answer);
    }

    return byQuestion;
  }, [record.surveyAnswers]);

  /*
    The item number is the LABEL, so it renders in the body face rather than mono. That is not
    the "dates and codes are always font-mono" rule being skipped: that rule governs a row's
    VALUE — `mono` on `PreviewRow` styles the value column, and the label column is prose by
    design at every call site. The tint is what marks this column as the key.
  */
  const rows: PreviewRow[] = (questions ?? []).map((question) => {
    const answer = answers.get(question.id) ?? null;

    return {
      label: question.number,
      value: (
        <div className="flex min-w-0 items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-slate-600">{question.text}</span>

            {/*
              The answer sits UNDER the question it answers, which is the whole shape of the
              panel: a reader scans the left rail for an item, reads the question, and the
              answer is the next line rather than a column away.
            */}
            {answer ? (
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium text-slate-800">{answer.answer ? 'Yes' : 'No'}</span>

                {/*
                  Only the follow-ups that actually carry a value. Rendering an em dash for each
                  one the question asks would put three empty lines under every plain "No" — the
                  full set of what a question asks for is on the questionnaire's own page.
                */}
                {surveyFollowUps(answer).map((line) => (
                  <span key={line.label} className="text-xs text-slate-500">
                    <span className="text-slate-400">{line.label}</span>{' '}
                    {/*
                      A date and an ID number are machine values, so they take the mono face
                      here exactly as they would in a cell or a preview table — "every date and
                      every code, on every surface". The details, the case status and the
                      country are prose and stay in the body face.
                    */}
                    <span className={line.mono ? 'font-mono tabular-nums' : undefined}>
                      {line.value}
                    </span>
                  </span>
                ))}

                {/*
                  Who said this, and when. It sits UNDER the answer and its follow-ups because
                  it is provenance for that answer rather than part of it — a step lighter than
                  the follow-ups above it, which are the content.

                  Only on an answered row: an unanswered question has no row to have a trail.
                */}
                <AnswerProvenance answer={answer} />
              </span>
            ) : (
              /*
                Not an em dash: the record has no answer, which is a different fact from a field
                that does not apply — and it is the one the reader is scanning for.
              */
              <span className="text-slate-400">Not answered</span>
            )}
          </div>

          {/* Hidden, not disabled, from a reader who cannot write the record. */}
          <PermissionGate permission="personnel.update">
            <Tooltip
              label={
                record.isActive
                  ? null
                  : 'This employee is deleted. Restore them before editing the record.'
              }
            >
              <Button
                id={`personnel-survey-update-${question.id}`}
                variant="secondary"
                onClick={() => onUpdate(question, answer)}
                disabled={!record.isActive}
              >
                Update
              </Button>
            </Tooltip>
          </PermissionGate>
        </div>
      ),
    };
  });

  return (
    <ProfileSection>
      {/* The four states, in the order they resolve. */}
      {error ? (
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : questions === null ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : questions.length === 0 ? (
        /*
          The one empty case left, and it is about the QUESTIONNAIRE rather than this record:
          with no questions there is nothing to answer, and the fix is on another page.
        */
        <EmptyState message="No questions on the survey. The form's items are maintained under Personnel → Settings → Survey." />
      ) : (
        /*
          The item number is a three-character code, so its column is FIXED rather than the
          proportional 38% a label of prose takes — at 38% the key column ran six times wider
          than anything in it while the question beside it wrapped.
        */
        <PreviewTable variant="table-display" labelWidth="narrow" rows={rows} />
      )}
    </ProfileSection>
  );
}

/**
 * One line of provenance for one answer: **the update where there is one, the creation where
 * there is not.** Never two — a correction is what the reader is being told about, and an
 * answer nobody has touched since it was recorded is still the one its author gave, so the
 * creator IS the attribution.
 *
 * **THIS IS NOW THE ONLY PLACE IN THE PRODUCT THAT ALTERNATES, and the argument that used to
 * carry it has expired.** It read *"the words are `<ProfileHeader>`'s, verbatim… that header is
 * three inches up the same screen carrying the record's own provenance in exactly this shape,
 * and two wordings on one page is the failure the one-word-product-wide rule exists to stop."*
 * `<ProfileHeader>` stopped alternating on 29 Sep 2026 and says `Created by` unconditionally —
 * so the header strip above this panel and an updated answer inside it can now be labelled
 * differently, which is the very thing that sentence was preventing.
 *
 * What is left is narrower and is this panel's own: provenance for ONE ANSWER among twelve,
 * where the correction is the news, rather than a record header under a status pill whose line
 * has to read the same across two profiles. **Whether it should follow the header is an open
 * product question** — see CLAUDE.md, where it is raised rather than answered.
 *
 * The OTHER half is not on this panel, and there is nowhere here to put it: a `<PreviewTable>`
 * row has no Other Info popover, which is where every `<DataTablePanel>` row in the 201 file
 * files both halves. That is the cost of this shape rather than an oversight.
 */
function AnswerProvenance({ answer }: { answer: PersonnelSurveyAnswer }) {
  const updated = Boolean(answer.updatedByName) || Boolean(answer.updatedAt);

  const actor = updated ? answer.updatedByName : answer.createdByName;
  const instant = updated ? answer.updatedAt : answer.createdAt;
  const verb = updated ? 'Updated' : 'Created';

  return (
    <span className="mt-0.5 text-xs text-slate-400">
      {/* No actor recorded still says when — "Created · <date>" reads correctly. */}
      {actor ? `${verb} by ${actor}` : verb}
      {instant ? (
        <>
          <span aria-hidden="true" className="px-1 text-slate-300">
            ·
          </span>
          {/* An instant, so it converts to the reader's zone — and it is mono, like every date. */}
          <time dateTime={toIso(instant)} className="font-mono tabular-nums">
            {formatDateTime(instant)}
          </time>
        </>
      ) : null}
    </span>
  );
}
